import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
// components/hr/TeamMemberEditModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  Button,
  Grid,
  Alert,
  FormHelperText,
  CircularProgress,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy';
import {
  User as PersonOutlined,
  Briefcase as WorkOutlined,
  CurrencyDollar as AttachMoneyOutlined,
  House as HomeOutlined,
  AddressBook as ContactEmergencyOutlined,
  CheckCircle as CheckCircleOutlined,
} from '@phosphor-icons/react';
import { useTeamMember, useUpdateTeamMember } from '../../hooks/useHr';
import type { TeamMember, MemberType, TeamMemberStatus } from '../../types/hr';

interface TeamMemberEditModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string | null;
  onSuccess?: () => void;
}

export const TeamMemberEditModal: React.FC<TeamMemberEditModalProps> = ({
  open,
  onClose,
  memberId,
  onSuccess,
}) => {
  const { data: member, isLoading: loadingMember } = useTeamMember(memberId || '');
  const updateMember = useUpdateTeamMember();

  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState<Partial<TeamMember>>({});

  // Initialize form data when member loads
  useEffect(() => {
    if (member) {
      setFormData({
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        phone: member.phone,
        member_type: member.member_type,
        status: member.status,
        job_title: member.job_title,
        department: member.department,
        hire_date: member.hire_date,
        start_date: member.start_date,
        hourly_rate: member.hourly_rate,
        salary: member.salary,
        commission_rate: member.commission_rate,
        address_line_1: member.address_line_1,
        address_line_2: member.address_line_2,
        city: member.city,
        state: member.state,
        zip_code: member.zip_code,
        emergency_contact_name: member.emergency_contact_name,
        emergency_contact_phone: member.emergency_contact_phone,
        emergency_contact_relationship: member.emergency_contact_relationship,
        bio: member.bio,
        notes: member.notes,
      });
    }
  }, [member]);

  // Reset tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!memberId) return;

    try {
      await updateMember.mutateAsync({ id: memberId, data: formData });
      onSuccess?.();
      onClose();
    } catch (err) {
      const details = (err as { responseData?: Record<string, unknown> })?.responseData;
      setError(details ? Object.entries(details).map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(', ') : String(value)}`).join('; ') : err instanceof Error ? err.message : 'Failed to update team member. Please try again.');
    }
  };

  const updateField = <K extends keyof TeamMember>(field: K, value: TeamMember[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!memberId) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 700,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <ModalClose />
        <DialogTitle>Edit Team Member</DialogTitle>

        <DialogContent>
          {loadingMember ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !member ? (
            <Alert color="danger">Failed to load team member data</Alert>
          ) : (
            <>
              {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
                <TabList>
                  <Tab><PersonOutlined style={{ marginRight: 4 }} /> Personal</Tab>
                  <Tab><WorkOutlined style={{ marginRight: 4 }} /> Employment</Tab>
                  <Tab><AttachMoneyOutlined style={{ marginRight: 4 }} /> Compensation</Tab>
                  <Tab><HomeOutlined style={{ marginRight: 4 }} /> Address</Tab>
                  <Tab><ContactEmergencyOutlined style={{ marginRight: 4 }} /> Emergency</Tab>
                </TabList>

                {/* Personal Information */}
                <TabPanel value={0}>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                      <FormControl required>
                        <FormLabel>First Name</FormLabel>
                        <Input
                          value={formData.first_name || ''}
                          onChange={(e) => updateField('first_name', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl required>
                        <FormLabel>Last Name</FormLabel>
                        <Input
                          value={formData.last_name || ''}
                          onChange={(e) => updateField('last_name', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl required>
                        <FormLabel>Email</FormLabel>
                        <Input
                          type="email"
                          value={formData.email || ''}
                          onChange={(e) => updateField('email', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Phone</FormLabel>
                        <Input
                          value={formData.phone || ''}
                          onChange={(e) => updateField('phone', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Bio</FormLabel>
                        <Textarea
                          minRows={2}
                          value={formData.bio || ''}
                          onChange={(e) => updateField('bio', e.target.value)}
                          placeholder="Brief bio or description"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>

                {/* Employment Details */}
                <TabPanel value={1}>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Employment Type</FormLabel>
                        <Select
                          value={formData.member_type || 'w2'}
                          onChange={(_, value) => updateField('member_type', value as MemberType)}
                        >
                          <Option value="w2">W-2 Employee</Option>
                          <Option value="1099">1099 Contractor</Option>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Status (use Terminate in the directory for offboarding)</FormLabel>
                        <Select
                          value={formData.status || 'pending'}
                          onChange={(_, value) => updateField('status', value as TeamMemberStatus)}
                        >
                          <Option value="pending">Pending</Option>
                          <Option value="onboarding">Onboarding</Option>
                          <Option value="active">Active</Option>
                          <Option value="inactive">Inactive</Option>
                          {member?.status === 'terminated' && <Option value="terminated">Terminated</Option>}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Job Title</FormLabel>
                        <Input
                          value={formData.job_title || ''}
                          onChange={(e) => updateField('job_title', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Department</FormLabel>
                        <Input
                          value={formData.department || ''}
                          onChange={(e) => updateField('department', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Hire Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.hire_date || ''}
                          onChange={(e) => updateField('hire_date', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Start Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.start_date || ''}
                          onChange={(e) => updateField('start_date', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Notes</FormLabel>
                        <Textarea
                          minRows={2}
                          value={formData.notes || ''}
                          onChange={(e) => updateField('notes', e.target.value)}
                          placeholder="Internal notes about this team member"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>

                {/* Compensation */}
                <TabPanel value={2}>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Hourly Rate ($)</FormLabel>
                        <Input
                          type="number"
                          value={formData.hourly_rate || ''}
                          onChange={(e) => updateField('hourly_rate', parseFloat(e.target.value) || undefined)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Annual Salary ($)</FormLabel>
                        <Input
                          type="number"
                          value={formData.salary || ''}
                          onChange={(e) => updateField('salary', parseFloat(e.target.value) || undefined)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Commission Rate (%)</FormLabel>
                        <Input
                          type="number"
                          value={formData.commission_rate || ''}
                          onChange={(e) => updateField('commission_rate', parseFloat(e.target.value) || undefined)}
                        />
                        <FormHelperText>
                          Also updates their login's rate, which is what job commissions are paid from.
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>

                {/* Address */}
                <TabPanel value={3}>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Address Line 1</FormLabel>
                        <AddressAutocompleteInput
                          value={formData.address_line_1 || ''}
                          onChange={(value) => updateField('address_line_1', value)}
                          onAddressSelect={(parsed) => setFormData(previous => ({ ...previous, address_line_1: parsed.address_1, city: parsed.city, state: parsed.state, zip_code: parsed.zip_code }))}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Address Line 2</FormLabel>
                        <Input
                          value={formData.address_line_2 || ''}
                          onChange={(e) => updateField('address_line_2', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>City</FormLabel>
                        <Input
                          value={formData.city || ''}
                          onChange={(e) => updateField('city', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <FormControl>
                        <FormLabel>State</FormLabel>
                        <Input
                          value={formData.state || ''}
                          onChange={(e) => updateField('state', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <FormControl>
                        <FormLabel>ZIP Code</FormLabel>
                        <Input
                          value={formData.zip_code || ''}
                          onChange={(e) => updateField('zip_code', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>

                {/* Emergency Contact */}
                <TabPanel value={4}>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Contact Name</FormLabel>
                        <Input
                          value={formData.emergency_contact_name || ''}
                          onChange={(e) => updateField('emergency_contact_name', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Relationship</FormLabel>
                        <Input
                          value={formData.emergency_contact_relationship || ''}
                          onChange={(e) => updateField('emergency_contact_relationship', e.target.value)}
                          placeholder="e.g., Spouse, Parent, Sibling"
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Phone Number</FormLabel>
                        <Input
                          value={formData.emergency_contact_phone || ''}
                          onChange={(e) => updateField('emergency_contact_phone', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Tabs>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={updateMember.isPending}
            startDecorator={<CheckCircleOutlined />}
            disabled={loadingMember || !member}
          >
            Save Changes
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default TeamMemberEditModal;
